import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Mail, Phone, KeyRound, Check, ArrowRight, Loader2 } from "lucide-react";
import { puvioService } from "../genesis/identity/puvioService";
import { toast } from "sonner";

export default function RecoverySheet({ open, onClose }) {
  const [method, setMethod] = useState("email");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [requestId, setRequestId] = useState(null);

  const submit = async () => {
    if (!contact.trim()) {
      toast.error("Please enter your " + (method === "email" ? "email" : "phone number"));
      return;
    }
    setBusy(true);
    try {
      const r = await puvioService.startRecovery(method, contact.trim());
      setRequestId(r.request_id);
      toast.success("Recovery request created");
    } catch (e) {
      toast.error(e?.message || "Couldn't start recovery");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setContact(""); setRequestId(null); };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) { reset(); onClose?.(); } }}>
      <SheetContent side="bottom" className="rounded-t-[28px] border-0 bg-card max-h-[80vh] overflow-y-auto" data-testid="recovery-sheet">
        <SheetHeader className="text-left pb-2">
          <div className="inline-flex w-fit items-center gap-1.5 bg-primary/10 text-primary rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider mb-2">
            <KeyRound size={11} /> Puvio Recovery
          </div>
          <SheetTitle className="text-[26px] font-extrabold tracking-tight">Account recovery</SheetTitle>
          <SheetDescription className="text-[13px] text-muted-foreground font-medium">
            Lost access to your Puvio ID? Pick a method, we'll guide you back in.
          </SheetDescription>
        </SheetHeader>

        {!requestId ? (
          <div className="mt-3 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMethod("email")}
                data-testid="recovery-method-email"
                className={`rounded-2xl border p-3 text-left transition ${method === "email" ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
              >
                <Mail size={16} className={method === "email" ? "text-primary" : "text-muted-foreground"} />
                <p className="text-[13px] font-bold mt-1.5">Email link</p>
                <p className="text-[11px] text-muted-foreground">We send a recovery link</p>
              </button>
              <button
                type="button"
                onClick={() => setMethod("phone")}
                data-testid="recovery-method-phone"
                className={`rounded-2xl border p-3 text-left transition ${method === "phone" ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
              >
                <Phone size={16} className={method === "phone" ? "text-primary" : "text-muted-foreground"} />
                <p className="text-[13px] font-bold mt-1.5">SMS code</p>
                <p className="text-[11px] text-muted-foreground">A code via text</p>
              </button>
            </div>

            <div>
              <Label htmlFor="recovery-contact" className="text-[11px] uppercase tracking-wider font-extrabold text-muted-foreground">
                {method === "email" ? "Email address" : "Phone number"}
              </Label>
              <Input
                id="recovery-contact"
                type={method === "email" ? "email" : "tel"}
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={method === "email" ? "you@example.com" : "+91 99999 99999"}
                className="mt-1.5 h-12 rounded-2xl"
                data-testid="recovery-contact-input"
              />
            </div>

            <Button
              type="button"
              onClick={submit}
              disabled={busy}
              className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-60"
              data-testid="recovery-submit"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <>Start recovery <ArrowRight size={14} className="ml-1" /></>}
            </Button>
          </div>
        ) : (
          <div className="mt-4 text-center space-y-3" data-testid="recovery-success">
            <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Check size={28} className="text-primary" />
            </div>
            <p className="text-[18px] font-extrabold tracking-tight">Recovery started</p>
            <p className="text-[12.5px] text-muted-foreground font-medium">
              Request ID <span className="font-extrabold text-foreground">{requestId}</span><br />
              We'll guide you back in once verification completes. (Puvio backend dispatches the code.)
            </p>
            <Button variant="outline" className="rounded-full" onClick={() => onClose?.()} data-testid="recovery-close">Close</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
